// Типы под backend ответы (см. astramarket-backend/src/astramarket/api/v1/*)
// Бэкенд отдаёт snake_case — типизируем как есть.

export type PageMeta = {
  total: number;
  page: number;
  page_size: number;
};

export type Page<T> = {
  items: T[];
  meta: PageMeta;
};

// ─── auth ──────────────────────────────────────────────────────────────────

export type UserOut = {
  id: number;
  login: string;
  full_name: string;
  role: string; // "admin" | "dev" | "local_admin" | "user"
  group_code: string | null;
  must_change?: boolean;
};

export type LoginRequest = {
  login: string;
  password: string;
};

export type LoginResponse = {
  token: string;
  user: UserOut;
};

export type MeResponse = {
  user: UserOut;
};

export type DevSettingsOut = {
  full_name: string;
  email: string;
  notify_publish: boolean;
  notify_revoke: boolean;
  notify_stats: boolean;
};

// ─── apps ──────────────────────────────────────────────────────────────────

export type AppOut = {
  pkg: string;
  name: string;
  short_description: string | null;
  developer_login: string;
  visibility: string;
  icon_color: string;
  icon_letter: string;
  category: string | null;
  current_version: string | null;
  current_gen: number | null;
  status: string; // "published" | "revoked" | "draft"
  revoked_reason: string | null;
  installs: number;
  downloads_30d: number;
  size_mb: number;
  updated_at: string | null;
};

export type VersionOut = {
  version: string;
  generation: number;
  changelog: string | null;
  size_mb: number | null;
  published_at: string;
  status: string; // "current" | "previous" | "revoked"
  has_delta: boolean;
};

export type AppLocaleOut = {
  code: string;
  name: string;
  title: string;
  short_description: string | null;
  complete: boolean;
  reviewed: boolean;
};

export type AppLocaleCreate = Omit<AppLocaleOut, "short_description"> & {
  short_description?: string | null;
};

export type AppLocaleUpdate = Omit<AppLocaleCreate, "code">;

export type AppDetailOut = AppOut & {
  versions: VersionOut[];
  locales?: AppLocaleOut[];
};

export type PublishVersionRequest = {
  version: string;
  generation: number;
  changelog?: string | null;
  size_mb: number;
};

export type RevokeRequest = {
  reason?: string | null;
};

// Тело запроса для POST /apps. developer_login — опционально:
// если не передан, бэкенд подставит current_user.
export type AppCreate = {
  pkg: string;
  name: string;
  short_description?: string | null;
  developer_login?: string | null;
  visibility?: string;
  icon_color?: string | null;
  icon_letter?: string | null;
  category?: string | null;
};

// ─── users / groups ────────────────────────────────────────────────────────

export type UserCreate = {
  login: string;
  full_name: string;
  role: string;
  group_code?: string | null;
  password: string;
};

export type GroupOut = {
  code: string;
  letters: string;
  name: string;
  devices: number;
  apps: number;
  description: string | null;
};

// ─── locals ────────────────────────────────────────────────────────────────

export type LocalOut = {
  id: string;
  location: string;
  address: string;
  status: string; // "online" | "stale" | "offline"
  clients: number;
  cache_gb: number;
  cache_cap_gb: number;
  last_sync_min: number;
  generation: number;
  cert_expires: string;
};

export type LocalsSummary = {
  total: number;
  online: number;
  stale: number;
  offline: number;
};

// Тело запроса для POST /locals
export type LocalCreate = {
  id: string;
  location: string;
  address: string;
  status?: string; // online | stale | offline (default offline)
  clients?: number;
  cache_gb?: number;
  cache_cap_gb?: number;
  last_sync_min?: number;
  generation?: number;
  cert_expires?: string;
};

// ─── events ────────────────────────────────────────────────────────────────

export type EventOut = {
  id: string;
  time_h: string;
  when_rel: string;
  color: string; // "green" | "red" | "blue" | "amber" | "gray"
  who: string;
  text: string;
  detail: string | null;
  type: string;
  source: string;
  target: string | null;
};

// ─── keys ──────────────────────────────────────────────────────────────────

export type KeyOut = {
  id: string;
  name: string;
  issued: string;
  expires: string;
  status: string; // "active" | "revoked"
  sha256: string;
  signs_apps: number;
};

export type RotateKeyRequest = {
  new_id: string;
  new_name: string;
};

// ─── settings ──────────────────────────────────────────────────────────────

export type SettingsOut = {
  items: Record<string, string>;
};

export type SettingsUpdate = {
  items: Record<string, string>;
};

// ─── analytics / security ──────────────────────────────────────────────────

export type ActiveSession = {
  login: string;
  ip: string | null;
  last_seen: string;
  requests: number;
  seconds_ago: number;
};

export type AnalyticsOverview = {
  total_requests: number;
  avg_latency_ms: number;
  p50: number;
  p95: number;
  p99: number;
  error_rate: number;
  by_status: Record<string, number>; // {"2xx":N,"3xx":N,"4xx":N,"5xx":N}
  window_hours: number;
};

export type EndpointStats = {
  path: string;
  count: number;
  avg_latency_ms: number;
  p50: number;
  p95: number;
  p99: number;
  error_rate: number;
  errors: number;
};

export type TimeBucket = {
  bucket_start: string;
  requests: number;
  errors: number;
  avg_latency_ms: number;
};

export type ErrorEntry = {
  id: number;
  timestamp: string;
  method: string;
  path: string;
  status_code: number;
  latency_ms: number;
  user_login: string | null;
  ip: string | null;
  error_code: string | null;
};

export type FailedLoginIp = {
  ip: string;
  failed_logins: number;
};

export type SecurityOverview = {
  failed_logins_24h: number;
  unauthorized_401: number;
  forbidden_403: number;
  unique_ips_24h: number;
  top_failed_login_ips: FailedLoginIp[];
  window_hours: number;
};

// ─── admin (KPI-сводка и сайдбар-счётчики) ─────────────────────────────────
// см. astramarket-backend/src/astramarket/api/v1/admin/schemas.py

export type AdminKpis = {
  generation: number;

  users_total: number;
  active_users_24h: number;
  active_users_14d_history: number[];

  apps_published: number;
  apps_revoked: number;
  apps_draft: number;
  apps_published_history: number[];

  groups_total: number;

  locals_total: number;
  locals_online: number;
  locals_stale: number;
  locals_offline: number;
  locals_online_history: number[];

  avg_sync_minutes: number;
  avg_sync_history: number[];
  sync_target_minutes: number;
};

export type AdminSidebar = {
  apps_total: number;
  users_total: number;
  groups_total: number;
  locals_total: number;
};

// ─── local node ────────────────────────────────────────────────────────────
// см. astramarket-backend/src/astramarket/api/v1/local/schemas.py

export type LocalResources = {
  cpu_percent: number;
  mem_percent: number;
  mem_used_mb: number;
  mem_total_mb: number;
};

export type LocalStatusOut = {
  local_id: string;
  status: string; // "online" | "stale" | "offline"
  uptime_seconds: number;
  generation: number;
  last_sync_min: number;
  peers_count: number;
  resources: LocalResources;
};

export type LocalSyncHistoryItem = {
  id: string;
  time_h: string;
  when_rel: string;
  text: string;
  detail: string | null;
  type: string; // "sync.requested" | "sync.complete" | "sync.failed" | ...
  source: string;
  target: string | null;
};

export type LocalSyncTriggerResponse = {
  ok: boolean;
  event_id: string;
  message: string;
};

export type LocalCachedPackage = {
  pkg: string;
  name: string;
  version: string;
  size_mb: number;
  generation: number;
};

export type LocalCacheDeleteResponse = {
  ok: boolean;
  pkg: string;
  event_id: string;
};

export type LocalServiceOut = {
  name: string;
  status: string; // "running" | "stopped" | "failed"
  pid: number | null;
  description: string | null;
};

export type LocalPeerLatencyPoint = {
  ts: string;
  latency_ms: number;
};

export type LocalPeerLink = {
  peer_id: string;
  peer_location: string;
  status: string; // "active" | "degraded" | "down"
  latency_ms: number;
  last_sync_at: string | null;
  timeseries: LocalPeerLatencyPoint[];
};

// ─── dev (кабинет разработчика) ────────────────────────────────────────────

export type DevAppOut = {
  pkg: string;
  name: string;
  status: string; // "published" | "revoked" | "draft"
  current_version: string | null;
  current_gen: number | null;
  installs: number;
  downloads_30d: number;
  size_mb: number;
  icon_color: string;
  icon_letter: string;
};

export type StatsByDayPoint = {
  date: string; // YYYY-MM-DD
  installs: number;
  errors: number;
};

export type DevStatsOut = {
  pkg: string;
  total_installs: number;
  total_errors: number;
  success_rate: number; // 0..1
  window_days: number;
  by_day: StatsByDayPoint[];
};

export type TokenOut = {
  id: number;
  name: string;
  scopes: string[];
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
};

export type TokenCreateRequest = {
  name: string;
  scopes?: string[];
};

export type TokenCreateResponse = {
  id: number;
  name: string;
  scopes: string[];
  created_at: string;
  token: string; // plain-секрет, выдаётся один раз
};

export type TokenRevokeResponse = {
  id: number;
  revoked_at: string;
};

// ─── ошибки ─────────────────────────────────────────────────────────────────

export class ApiError extends Error {
  status: number;
  code: string;
  details: unknown;

  constructor(status: number, code: string, message: string, details: unknown = null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}
